import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    Box,
    TextField,
    Button,
    Collapse,
    IconButton,
    TableSortLabel,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNotify } from 'react-admin';
import {
    getAdminOvertimeRequests,
    OvertimeRequest,
    OvertimeHistoryEntry,
    AdminOvertimeFilters,
} from '../dataProvider';

/**
 * Formats an overtime duration into a human-readable string.
 *
 * @param hours   - Integer hours component (0-23).
 * @param minutes - Minutes component (0, 15, 30, or 45).
 * @returns A string like "2h 30m" or "1h" or "45m".
 *
 * @example
 * formatDuration(2, 30); // "2h 30m"
 * formatDuration(0, 45); // "45m"
 */
function formatDuration (hours: number, minutes: number): string {
    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.join(' ') || '0h';
}

/**
 * Sort order type for table columns.
 */
type SortOrder = 'asc' | 'desc';

/**
 * Props for a single expandable declaration row.
 */
interface DeclarationRowProps {
    /** The overtime declaration to display. */
    row: OvertimeRequest;
}

/**
 * A single expandable table row displaying an overtime declaration.
 *
 * Shows the declaration details and a collapsible history timeline.
 *
 * @param props - The component props.
 * @returns The rendered table row with optional expanded history panel.
 */
const DeclarationRow: React.FC<DeclarationRowProps> = ({ row }) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <TableRow hover>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{row.full_name || row.employee_id}</TableCell>
                <TableCell>{row.date}</TableCell>
                <TableCell>{row.start_time}</TableCell>
                <TableCell>{formatDuration(row.duration_hours, row.duration_minutes)}</TableCell>
                <TableCell>{row.task_description}</TableCell>
                <TableCell>
                    <Chip
                        label={row.status}
                        size="small"
                        color={row.status === 'Approved' ? 'success' : 'default'}
                    />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                History
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Action</TableCell>
                                        <TableCell>Actor</TableCell>
                                        <TableCell>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(row.history ?? []).map((h: OvertimeHistoryEntry) => (
                                        <TableRow key={h.id}>
                                            <TableCell>{h.action}</TableCell>
                                            <TableCell>{h.actor_name ?? h.actor_id}</TableCell>
                                            <TableCell>
                                                {h.created_date
                                                    ? new Date(h.created_date).toLocaleString()
                                                    : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(row.history ?? []).length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                No history records
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

/**
 * OvertimeAdminList component for managers/admins to view all employees' overtime.
 *
 * Provides a filterable table with:
 * - **Name filter**: Substring search on employee full name
 * - **Date range filter**: Inclusive start/end date pickers
 * - **Sortable columns**: Date (default descending) and Employee Name
 * - **Expandable rows**: Inline history timeline per declaration
 * - **Status badges**: Color-coded chips (success for Approved, default for Cancelled)
 *
 * Requires the `overtime.view_all` permission to function; the parent route
 * should gate access accordingly.
 */
export const OvertimeAdminList: React.FC = () => {
    const notify = useNotify();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [declarations, setDeclarations] = useState<OvertimeRequest[]>([]);
    const [sortField, setSortField] = useState<'date' | 'full_name'>('date');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

    // Filter state
    const [nameFilter, setNameFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    /**
     * Fetches overtime declarations from the API with the current filter state.
     */
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const filters: AdminOvertimeFilters = {};
            if (nameFilter.trim()) filters.name = nameFilter.trim();
            if (dateFrom) filters.date_from = dateFrom;
            if (dateTo) filters.date_to = dateTo;
            const data = await getAdminOvertimeRequests(filters);
            setDeclarations(data);
        } catch (err: unknown) {
            const msg = (err as { message?: string })?.message || 'Failed to load overtime declarations';
            setError(msg);
            notify(msg, { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [nameFilter, dateFrom, dateTo, notify]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    /**
     * Handles the filter form submission.
     *
     * @param e - The form submit event.
     */
    const handleFilterSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        fetchData();
    };

    /**
     * Clears all filter fields and re-fetches the data.
     */
    const handleClearFilters = () => {
        setNameFilter('');
        setDateFrom('');
        setDateTo('');
    };

    /**
     * Handles sorting by a column.
     *
     * @param field - The field to sort by.
     */
    const handleSort = (field: 'date' | 'full_name') => {
        if (sortField === field) {
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortOrder(field === 'date' ? 'desc' : 'asc');
        }
    };

    // Sort declarations client-side
    const sortedDeclarations = [...declarations].sort((a, b) => {
        const valA = sortField === 'date' ? a.date : (a.full_name || a.employee_id);
        const valB = sortField === 'date' ? b.date : (b.full_name || b.employee_id);
        const cmp = valA < valB ? -1 : valA > valB ? 1 : 0;
        return sortOrder === 'asc' ? cmp : -cmp;
    });

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    All Overtime Declarations
                </Typography>

                {/* Filter bar */}
                <Box
                    component="form"
                    onSubmit={handleFilterSubmit}
                    display="flex"
                    gap={2}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ mb: 3 }}
                >
                    <TextField
                        label="Employee Name"
                        size="small"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        inputProps={{ 'aria-label': 'Employee Name' }}
                    />
                    <TextField
                        label="From Date"
                        type="date"
                        size="small"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'aria-label': 'From Date' }}
                    />
                    <TextField
                        label="To Date"
                        type="date"
                        size="small"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'aria-label': 'To Date' }}
                    />
                    <Button type="submit" variant="contained" size="small">
                        Filter
                    </Button>
                    <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        onClick={handleClearFilters}
                    >
                        Clear
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : declarations.length === 0 ? (
                    <Alert severity="info">
                        No overtime declarations found.
                    </Alert>
                ) : (
                    <TableContainer component={Paper}>
                        <Table aria-label="all overtime declarations">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'full_name'}
                                            direction={sortField === 'full_name' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('full_name')}
                                        >
                                            Employee
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'date'}
                                            direction={sortField === 'date' ? sortOrder : 'desc'}
                                            onClick={() => handleSort('date')}
                                        >
                                            Date
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Start Time</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Task</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedDeclarations.map((decl) => (
                                    <DeclarationRow key={decl.id} row={decl} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};
